import React from 'react'
import {
  SYNTHETIC_ATHLETES,
  SyntheticAthlete,
  SYNTHETIC_FORM_COMPLIANCE_SUMMARY,
} from './syntheticDashboardData'
import { AvailabilitySegmentedBar } from './AvailabilitySegmentedBar'
import { WorkloadStackedChart } from './WorkloadStackedChart'
import { WellnessSmallCharts } from './WellnessSmallCharts'
import { BodyDiscomfortPanel } from './BodyDiscomfortPanel'
import { PendingActionsList } from './PendingActionsList'
import {
  Users,
  ShieldCheck,
  Flame,
  Moon,
  HeartPulse,
  FileCheck2,
  ExternalLink,
} from 'lucide-react'

interface TeamTrackingViewProps {
  onSelectAthlete?: (athlete: SyntheticAthlete) => void
  onNavigateToHealth?: () => void
  onNavigateToStats?: () => void
  isCoachView?: boolean
}

export const SyntheticTeamTrackingView: React.FC<TeamTrackingViewProps> = ({
  onSelectAthlete,
  onNavigateToHealth,
  onNavigateToStats,
  isCoachView = false,
}) => {
  const athletes = SYNTHETIC_ATHLETES
  const compliance = SYNTHETIC_FORM_COMPLIANCE_SUMMARY

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* KPI Row (5 metrics + Compact Compliance Card) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '0.85rem',
      }}>
        {/* KPI 1: Jugadores en seguimiento activo */}
        <div style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '8px',
          padding: '0.9rem 1rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              Seguimiento Activo
            </span>
            <Users size={15} color="var(--nav-bg-base, #0a1320)" />
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
            20 <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>atletas</span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>
            Plantilla convocada · n=20
          </div>
        </div>

        {/* KPI 2: Disponibilidad sanitaria publicada */}
        <div style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '8px',
          padding: '0.9rem 1rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              Disponibilidad
            </span>
            <ShieldCheck size={15} color="#15803d" />
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>
            85% <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>(17/20 aptos)</span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>
            2 restringidos · 1 baja médica
          </div>
        </div>

        {/* KPI 3: Carga semanal media sRPE */}
        <div style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '8px',
          padding: '0.9rem 1rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              Carga Semanal Media
            </span>
            <Flame size={15} color="#d97706" />
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
            530 <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>min</span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>
            3.445 UA (sRPE) · -150m vs S7 · n=18
          </div>
        </div>

        {/* KPI 4: Horas medias de sueño (o Carga deportiva si es vista coach) */}
        <div style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '8px',
          padding: '0.9rem 1rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              {isCoachView ? 'Volumen en Campo' : 'Sueño Medio'}
            </span>
            <Moon size={15} color="#4338ca" />
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
            {isCoachView ? '210' : '7.4'}{' '}
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>
              {isCoachView ? 'min Flag' : 'h / noche'}
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>
            {isCoachView ? '40% del volumen semanal · n=18' : 'Calidad media 3.8/5 · n=18'}
          </div>
        </div>

        {/* KPI 5: Fatiga y Recuperación como métricas separadas */}
        <div style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '8px',
          padding: '0.9rem 1rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              {isCoachView ? 'Aptitud Colectiva' : 'Fatiga / Recuperación'}
            </span>
            <HeartPulse size={15} color="var(--brand-red, #b91c1c)" />
          </div>
          <div style={{ fontSize: '1.45rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
            {isCoachView ? 'Alta' : '2.8 · 3.7'}
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary, #475569)', marginLeft: '4px' }}>
              {isCoachView ? '(90% roster)' : 'escala 1..5'}
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', marginTop: '2px' }}>
            {isCoachView ? 'Sin restricciones colectivas' : 'Fatiga 2.8 vs Recup. 3.7 · n=18'}
          </div>
        </div>

        {/* KPI 6: Indicador compacto de cumplimiento de formularios */}
        <div style={{
          background: 'var(--surface-subtle, #f8fafc)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '8px',
          padding: '0.9rem 1rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
                Cumplimiento S8
              </span>
              <FileCheck2 size={15} color="#0284c7" />
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
              {compliance.submittedCount}/{compliance.totalExpected}{' '}
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#0284c7' }}>
                ({compliance.submittedPct}%)
              </span>
            </div>
          </div>

          <div style={{ marginTop: '6px' }}>
            {onNavigateToHealth ? (
              <button
                type="button"
                onClick={onNavigateToHealth}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: 'var(--brand-red, #b91c1c)',
                  cursor: 'pointer',
                }}
              >
                <span>Ver módulo Formularios</span>
                <ExternalLink size={11} />
              </button>
            ) : (
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted, #64748b)' }}>
                1 borrador · 1 pendiente
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Top Section: Availability Segmented Bar */}
      <AvailabilitySegmentedBar
        athletes={athletes}
        onSelectAthlete={onSelectAthlete}
        compact={false}
      />

      {/* Center Section: Workload Stacked Chart */}
      <WorkloadStackedChart
        onNavigateToStats={onNavigateToStats}
        compact={false}
      />

      {/* Wellness & Sleep Small Coordinated Charts */}
      {!isCoachView && <WellnessSmallCharts />}

      {/* Bottom Grid: Body Discomfort Panel & Pending Actions */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
        gap: '1.25rem',
        alignItems: 'start',
      }}>
        <BodyDiscomfortPanel
          onSelectAthlete={(athId) => {
            const found = athletes.find((a) => a.id === athId)
            if (found && onSelectAthlete) onSelectAthlete(found)
          }}
          isCoachView={isCoachView}
        />

        <PendingActionsList
          onSelectAction={(act) => {
            const found = athletes.find((a) => a.id === act.athleteId)
            if (found && onSelectAthlete) onSelectAthlete(found)
          }}
          compact={false}
        />
      </div>
    </div>
  )
}
